import { formatAmount } from "@/lib/utils";
import { RootState } from "@/redux/store";
import {
  useGetBudgetByCommunityData,
  useGetCommunityApprovalInfo,
  useGetCommunityApprovalLimit,
  useGetPositionApprovalLimit,
  useGetPositionList,
} from "@/services";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { toList } from "../consts";

const APPROVAL_LIMIT_CARDS = [
  {
    title: "CAM Approval Limits",
    data: [
      { label: "Invoice Limit", apiKey: "pmLimit" },
      { label: "Total Limit", apiKey: "pmTotalLimit" },
      { label: "Approved Amount", apiKey: "pmApprovedToDate" },
      { label: "Remaining Limit", apiKey: "pmRemainingLimit" },
    ],
  },
  {
    title: "Board Approval Limits",
    data: [
      { label: "Total Limit", apiKey: "boardTotalLimit" },
      { label: "Approved Amount", apiKey: "boardApprovedToDate" },
      { label: "Remaining Limit", apiKey: "boardRemainingLimit" },
    ],
  },
  {
    title: "Single Level Approval Limits",
    data: [
      { label: "Total Limit", apiKey: "singleLevelTotalLimit" },
      { label: "Approved Amount", apiKey: "singleLevelApprovedToDate" },
      { label: "Remaining Limit", apiKey: "singleLevelRemainingLimit" },
    ],
  },
];

const usePositionConfig = () => {
  const [globalFilter, setGlobalFilter] = useState("");
  const [year, setYear] = useState(moment().format("YYYY"));
  const [yearOptions, setYearOptions] = useState<any[]>([]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const communityDetail = useSelector(
    (state: RootState) => state.currentData.communityData
  );

  const { data: budgetYears, isLoading: isBudgetYearLoading } =
    useGetBudgetByCommunityData({
      communityId: communityDetail?.communityId as string,
      queryParams: {
        enabled: !!communityDetail?.communityId,
      },
    });

  useEffect(() => {
    if (budgetYears?.data) {
      const years = [
        ...new Set(budgetYears?.data?.map((data: any) => data?.year)),
      ].sort();

      setYearOptions(
        years?.map((year: any) => ({ label: year, value: year })) as any
      );
    }
  }, [budgetYears]);

  const { data: positionList, isLoading: isPositionListLoading } =
    useGetPositionList({
      communityId: communityDetail?.communityId as string,
      queryParams: {
        enabled: !!(communityDetail?.communityId && year),
      },
    });

  const { data: positionApprovalLimits, isLoading: isLimitLoading } =
    useGetPositionApprovalLimit({
      communityId: communityDetail?.communityId as string,
      year: year as string,
      queryParams: {
        enabled: !!(communityDetail?.communityId && year),
      },
    });

  const { data: communityLimits, isLoading: isCommunityLimitLoading } =
    useGetCommunityApprovalLimit({
      communityId: communityDetail?.communityId as string,
      year: year as string,
      queryParams: {
        enabled: !!(communityDetail?.communityId && year),
      },
    });

  const { data: approvalInfo, isLoading: isApprovalInfoLoading } =
    useGetCommunityApprovalInfo({
      community_id: communityDetail?.communityId as string,
      queryParams: {
        enabled: !!communityDetail?.communityId,
      },
    });

  const approvalLimits = useMemo(() => {
    const info = approvalInfo?.data || approvalInfo || {};
    const yearly = communityLimits?.data || communityLimits || {};
    const combined = {
      ...info,
      ...yearly,
      pmRemainingLimit:
        yearly?.pmRemainingLimit ??
        Number(info?.pmTotalLimit || 0) - Number(yearly?.pmApprovedToDate || 0),
      boardRemainingLimit:
        yearly?.boardRemainingLimit ??
        Number(info?.boardTotalLimit || 0) -
          Number(yearly?.boardApprovedToDate || 0),
      singleLevelRemainingLimit:
        yearly?.singleLevelRemainingLimit ??
        Number(info?.singleLevelTotalLimit || 0) -
          Number(yearly?.singleLevelApprovedToDate || 0),
    };

    return APPROVAL_LIMIT_CARDS.map((limit) => ({
      ...limit,
      data: limit.data.map((val) => ({
        ...val,
        value: formatAmount(combined?.[val.apiKey] || "0"),
      })),
    }));
  }, [approvalInfo, communityLimits]);

  const positionApprovalTableData = useMemo(() => {
    const positions = toList(positionList);
    const limits = toList(positionApprovalLimits);

    return positions.map((position: any) => {
      const matchingLimits = limits.filter(
        (limit: any) =>
          String(limit?.bomPositionId) === String(position?.bomPositionId)
      );
      const limit = matchingLimits[0] || {};
      const approvedToDate = matchingLimits.reduce(
        (sum: number, item: any) => sum + Number(item?.approvedToDate || 0),
        0
      );
      const remainingLimit = matchingLimits.reduce(
        (sum: number, item: any) => sum + Number(item?.remainingLimit || 0),
        0
      );

      return {
        ...position,
        ...limit,
        bomPositionId: position?.bomPositionId,
        positionName: position?.positionName || limit?.positionName,
        approveApInv: position?.approveApInv,
        approveApInvAmtLimit: position?.approveApInvAmtLimit,
        approveApInvAmtYearlyLimit: position?.approveApInvAmtYearlyLimit,
        approvalLimit: limit?.approvalLimit,
        totalLimit: limit?.totalLimit,
        approvedToDate,
        remainingLimit: matchingLimits?.length > 0 ? remainingLimit : position?.approveApInvAmtYearlyLimit,
      };
    });
  }, [positionList, positionApprovalLimits]);

  const columns = [
    {
      accessorKey: "positionName",
      header: <div>Position Name</div>,
      accessorFn: (row: any) => String(row.positionName || ""),
    },
    {
      accessorKey: "approvalLimit",
      header: <div>Invoice Approval Limit</div>,
      accessorFn: (row: any) =>
        formatAmount(
          row?.approveApInv ? String(row?.approveApInvAmtLimit || 0) : "0"
        ),
    },
    {
      accessorKey: "totalLimit",
      header: <div>Yearly Approval Limit</div>,
      accessorFn: (row: any) =>
        formatAmount(
          row?.approveApInv
            ? String(row?.approveApInvAmtYearlyLimit || row?.totalLimit || 0)
            : "0"
        ),
    },
    {
      accessorKey: "approvedToDate",
      header: <div>Total Approved Amount</div>,
      accessorFn: (row: any) =>
        formatAmount(String(row?.approvedToDate || 0)),
    },
    {
      accessorKey: "remainingLimit",
      header: <div>Remaining Approval Limit</div>,
      accessorFn: (row: any) =>
        formatAmount(String(row?.remainingLimit || 0)),
    },
  ];

  const onYearChange = (val: any) => {
    setYear(val);
  };

  return {
    globalFilter,
    setGlobalFilter,
    columns,
    positionApprovalLimits: positionApprovalTableData,
    year,
    onYearChange,
    communityLimits,
    isCommunityLimitLoading:
      isCommunityLimitLoading ||
      isApprovalInfoLoading ||
      isPositionListLoading ||
      isLimitLoading,
    approvalLimits,
    isBudgetYearLoading,
    yearOptions,
  };
};

export default usePositionConfig;
